import { FormInput } from "@/common/components/form/Form";
import { IFilterInput } from "@/common/types";

// MED-699: filter config for the buyer-facing catalogue ("all products" tab),
// consumed by the shared Table filter component the orders grid already uses.
// MED-713: expiry and price are range filters — the `range` metadata makes the
// shared Filter component reject an inverted range (from > to, min > max) before
// it's applied.
export const CatalogueFilterOptions: IFilterInput[] = [
  {
    name: "manufacturer_name",
    label: "Manufacturer",
    type: "text",
  },
  {
    name: "expiry_from",
    label: "Expiry From",
    type: "date",
    range: { group: "expiry", bound: "lower" },
  },
  {
    name: "expiry_to",
    label: "Expiry To",
    type: "date",
    range: { group: "expiry", bound: "upper" },
  },
  {
    name: "min_price",
    label: "Min Price",
    type: "text",
    range: { group: "price", bound: "lower", compare: "number" },
  },
  {
    name: "max_price",
    label: "Max Price",
    type: "text",
    range: { group: "price", bound: "upper", compare: "number" },
  },
  {
    name: "stock",
    label: "Availability",
    type: "radio",
    options: [
      { label: "In Stock", value: "in_stock" },
      { label: "Out of Stock", value: "out_of_stock" },
      { label: "All", value: "all" },
    ],
  },
];

// MED-699: sort options for the catalogue. "" (Newest) matches the backend's
// default order (created_at desc) when no sort_by is sent.
export const CatalogueSortOptions = [
  { label: "Newest", sortBy: "", sortDirection: "" },
  { label: "Name (A-Z)", sortBy: "name", sortDirection: "asc" },
  { label: "Name (Z-A)", sortBy: "name", sortDirection: "desc" },
  { label: "Price (Low to High)", sortBy: "price", sortDirection: "asc" },
  { label: "Price (High to Low)", sortBy: "price", sortDirection: "desc" },
  { label: "Expiry (Soonest First)", sortBy: "expiry_date", sortDirection: "asc" },
];

// Buyer-facing "quick order" panel (product-order-content.tsx) — badge
// styling per stock status. Colors match theme.status (the same level_1/4/5
// green/orange/red used for "delivered"/"pending"/"rejected" pills in the
// shared Table component), not arbitrary values, so this pill reads as the
// same "status" language as the rest of the app.
export const productStatusConfig = {
  available: {
    label: "Available",
    color: "#07d907",
    disabled: false,
  },
  "out-of-stock": {
    label: "Out of stock",
    color: "#ff5100",
    disabled: true,
  },
  "low-stock": {
    label: "Low stock",
    color: "#ffa500",
    disabled: false,
  },
};

// `price` and `packagingType` are at the seller's SELLING level. For a
// manufacturer MED-698 pins that to the product's outermost layer, but this
// catalogue also shows a distributor's stock, and a distributor who breaks
// bulk sells by the pack. `available` carries its own level already, rendered
// server-side by MED-697.
export function getProductOrderStats(product: {
  batchNo: string;
  expiryDate: string;
  // MED-697: pre-rendered by the server at this holder's selling level and
  // summed per batch, so it is read out as-is rather than counted here.
  available: string;
  price: string;
  packagingType: string;
}) {
  return [
    { label: "Batch No", value: product.batchNo },
    { label: "Expiry Date", value: product.expiryDate },
    { label: "Available", value: product.available },
    { label: "Price", value: `${product.price} / ${product.packagingType}` },
  ];
}

// Date/note fields for the quick-order panel (product-order-content.tsx),
// driven through Form's removeSubmitButton + externalUpdateData pattern (see
// product-factory/index.tsx for the reference usage). Quantity is no longer
// here — it's per-batch, picked via the MultiSelect batch picker instead
// (see ISelectedBatch in product-order-content.tsx), matching make-order/
// subs/ReqForm.tsx's batch-quantity pattern. notInTheFuture matches
// OrderDeliveryDetails' date field verbatim.
export const productOrderFormInputs: FormInput[] = [
  {
    name: "expectedDeliveryDate",
    inputType: "date",
    prompt: "Expected Delivery Date",
    required: true,
    fullWidth: true,
    rules: {
      notInTheFuture: {
        expectedValue: false,
        errorMessage: "Expected delivery date cannot be in the past",
      },
    },
  },
  {
    name: "orderNote",
    inputType: "textarea",
    prompt: "Order Note",
    placeholder: "Add a note for this order (optional)",
    fullWidth: true,
  },
];
